import { useCallback, useState } from 'react';
import './billing.css';
import { ChatScreen } from './components/ChatScreen.tsx';
import { Landing } from './components/Landing.tsx';
import { useEntitlement } from './hooks/useEntitlement.ts';
import {
  createTestStream,
  getCameraStream,
  mediaErrorText,
  stopStream,
  wantsTestStream,
} from './lib/media.ts';
import { loadSettings, saveSettings, type Settings } from './lib/settings.ts';

export type SettingsUpdate = (update: (current: Settings) => Settings) => void;

export function App() {
  const [settings, setSettings] = useState<Settings>(loadSettings);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { entitlement, apply, notice, dismissNotice } = useEntitlement();

  const updateSettings = useCallback<SettingsUpdate>((update) => {
    setSettings((current) => {
      const next = update(current);
      saveSettings(next);
      return next;
    });
  }, []);

  const start = async (useTestStream: boolean) => {
    setStarting(true);
    setError(null);
    try {
      const label = `Tab ${Math.floor(10 + Math.random() * 90)}`;
      setStream(
        import.meta.env.DEV && (useTestStream || wantsTestStream())
          ? createTestStream(label)
          : await getCameraStream(settings.devices),
      );
    } catch (cause) {
      setError(mediaErrorText(cause));
    } finally {
      setStarting(false);
    }
  };

  const exit = () => {
    stopStream(stream);
    setStream(null);
  };

  if (stream && settings.gender) {
    return (
      <ChatScreen
        localStream={stream}
        gender={settings.gender}
        settings={settings}
        entitlement={entitlement}
        onSettingsChange={updateSettings}
        onEntitlement={apply}
        onExit={exit}
      />
    );
  }

  return (
    <Landing
      settings={settings}
      starting={starting}
      error={error}
      entitlement={entitlement}
      notice={notice}
      onDismissNotice={dismissNotice}
      onSettingsChange={updateSettings}
      onStart={start}
    />
  );
}
